import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onExpand }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleDemoClick = (demoUrl) => {
    window.open(demoUrl, '_blank');
  };

  const handleGithubClick = (githubUrl) => {
    window.open(githubUrl, '_blank');
  };

  return (
    <div 
      className="bg-card border border-border rounded-xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Project Image */}
      <div className="relative overflow-hidden h-48">
        <Image
          src={project?.image}
          alt={project?.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Quick Actions Overlay */}
        <div className={`absolute top-4 right-4 flex space-x-2 transition-all duration-300 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
        }`}>
          {project?.demoUrl && (
            <Button
              variant="secondary"
              size="sm"
              iconName="ExternalLink"
              iconSize={14}
              onClick={() => handleDemoClick(project?.demoUrl)}
              className="bg-background/90 backdrop-blur-sm hover:bg-background"
            >
              Demo
            </Button>
          )}
          {project?.githubUrl && (
            <Button
              variant="outline"
              size="sm"
              iconName="Github"
              iconSize={14}
              onClick={() => handleGithubClick(project?.githubUrl)}
              className="bg-background/90 backdrop-blur-sm hover:bg-background"
            >
              Code
            </Button>
          )}
        </div>

        {/* Project Status Badge */}
        {project?.status && (
          <div className="absolute top-4 left-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              project?.status === 'Live' ?'bg-success text-success-foreground' 
                : project?.status === 'In Progress' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground'
            }`}>
              {project?.status}
            </span>
          </div>
        )}
      </div>
      {/* Project Content */}
      <div className="p-6">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xl font-bold text-primary group-hover:text-accent transition-colors duration-300">
              {project?.title}
            </h3>
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
              <Icon name="Calendar" size={12} />
              <span>{project?.year}</span>
            </div>
          </div>
          <p className="text-text-secondary text-sm leading-relaxed">
            {project?.description}
          </p>
        </div>

        {/* Technology Stack */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {project?.technologies?.slice(0, 4)?.map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-md"
              >
                {tech}
              </span>
            ))}
            {project?.technologies?.length > 4 && (
              <span className="px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded-md">
                +{project?.technologies?.length - 4} more
              </span>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        {project?.metrics && (
          <div className="mb-4">
            <div className="grid grid-cols-2 gap-4">
              {project?.metrics?.slice(0, 2)?.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="text-lg font-bold text-accent">{metric?.value}</div>
                  <div className="text-xs text-muted-foreground">{metric?.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Project Type & Industry */}
        <div className="mb-4 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Layers" size={12} />
            <span>{project?.type}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Building" size={12} />
            <span>{project?.industry}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            variant="default"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            iconSize={14}
            onClick={() => onExpand(project)}
            className="flex-1"
          >
            View Case Study
          </Button>
          {project?.demoUrl && (
            <Button
              variant="outline"
              size="sm"
              iconName="Play"
              iconSize={14}
              onClick={() => handleDemoClick(project?.demoUrl)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;